import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

export default function LeaveTypeCRUD() {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [newLeaveType, setNewLeaveType] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const navigate = useNavigate();

  // Fetch all leave types
  const fetchLeaveTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/leave-types/all`);
      const data = await res.json();
      setLeaveTypes(data);
    } catch (error) {
      toast.error("Failed to load leave types");
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  // Add new leave type
  const handleAdd = async () => {
    if (!newLeaveType.trim()) {
      toast.warning("Please enter a leave type name.");
      return;
    }

    const res = await fetch(`${API_BASE_URL}/api/leave-types/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newLeaveType }),
    });

    if (res.ok) {
      toast.success("Leave type added successfully!");
      setNewLeaveType("");
      fetchLeaveTypes();
    } else {
      const msg = await res.text();
      toast.error(msg);
    }
  };

  // Update leave type
  const handleUpdate = async (id) => {
    if (!editName.trim()) {
      toast.warning("Please enter a valid name.");
      return;
    }

    const res = await fetch(`${API_BASE_URL}/api/leave-types/update/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: editName }),
    });

    if (res.ok) {
      toast.success("Leave type updated!");
      setEditingId(null);
      fetchLeaveTypes();
    } else {
      const msg = await res.text();
      toast.error(msg);
    }
  };

  // Delete leave type
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this leave type?")) return;

    const res = await fetch(`${API_BASE_URL}/api/leave-types/delete/${id}`, {
      method: "DELETE",
    });

    if (res.ok) {
      toast.info("Leave type deleted.");
      fetchLeaveTypes();
    } else {
      const msg = await res.text();
      toast.error(msg);
    }
  };

  return (
    <>
      {/* ✅ Fixed Navbar */}
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      {/* ✅ Full-page layout */}
      <div
        className="d-flex flex-column p-4"
        style={{
          minHeight: "100vh",
          backgroundColor: "#F5F7FB",
          marginTop: "110px",
        }}
      >
        <h3 className="fw-bold text-dark mb-4 text-start">
          Leave Type Management
        </h3>

        <div className="row flex-grow-1 g-4">
          {/* ✅ Left Column - Add Leave Type */}
          <div className="col-md-4 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{
                borderRadius: "12px",
                height: "fit-content",
                alignSelf: "flex-start",
              }}
            >
              <div
                className="card-header fw-semibold"
                style={{
                  backgroundColor: "#DFE4EB",
                  color: "black",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                Add New Leave Type
              </div>
              <div className="card-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Enter leave type name"
                  value={newLeaveType}
                  onChange={(e) => setNewLeaveType(e.target.value)}
                />
                <button
                  className="btn text-dark w-40"
                  style={{ backgroundColor: "#DFE4EB" }}
                  onClick={handleAdd}
                >
                  <FaPlus className="me-2" />
                  Add Leave Type
                </button>
              </div>
            </div>
          </div>

          {/* ✅ Right Column - Leave Type List */}
          <div className="col-md-8 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{ borderRadius: "12px" }}
            >
              <div
                className="card-header fw-semibold d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#DFE4EB",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                <span>All Leave Types</span>
                <span className="badge bg-secondary">
                  {leaveTypes.length} Total
                </span>
              </div>

              <div
                className="card-body p-0"
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                <ul className="list-group list-group-flush">
                  {leaveTypes.length === 0 && (
                    <li className="list-group-item text-muted text-center py-3">
                      No leave types found.
                    </li>
                  )}
                  {leaveTypes.map((lt) => (
                    <li
                      key={lt.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {editingId === lt.id ? (
                        <input
                          type="text"
                          className="form-control me-2"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{ maxWidth: "300px" }}
                        />
                      ) : (
                        <span className="fw-medium">{lt.name}</span>
                      )}

                      <div>
                        {editingId === lt.id ? (
                          <>
                            <button
                              className="btn btn-success btn-sm me-2"
                              onClick={() => handleUpdate(lt.id)}
                            >
                              <FaSave /> Save
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}
                            >
                              <FaTimes /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn  btn-sm me-2"
                              onClick={() => {
                                setEditingId(lt.id);
                                setEditName(lt.name);
                              }}
                            >
                              <FaEdit /> 
                            </button>
                            <button
                              className="btn  btn-sm"
                              onClick={() => handleDelete(lt.id)}
                            >
                              <FaTrash /> 
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
